/**
 * pages/LoginPage.jsx
 *
 * Shown when the user is not logged in (401 from /api/auth/me).
 * Clicking "Sign in with Google" navigates to the backend OAuth endpoint
 * which redirects to Google and back.
 */

import React, { useEffect, useState } from 'react';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function LoginPage() {
  const [dark, setDark] = useState(() => {
    try { return localStorage.getItem('tt-theme') !== 'light'; } catch { return true; }
  });

  // Check for auth_error in URL query string (passed back from OAuth callback on failure)
  const [authError, setAuthError] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get('auth_error');
    if (err) {
      setAuthError(err);
      // Clean the URL so refreshing doesn't re-show the error
      window.history.replaceState({}, '', '/');
    }
  }, []);

  const handleLogin = () => {
    window.location.href = `${BASE}/api/auth/google`;
  };

  const errorMessages = {
    token_exchange:   'Could not exchange the authorisation code. Please try again.',
    userinfo:         'Could not retrieve your Google profile. Please try again.',
    missing_profile:  'Your Google account did not return an email address.',
    db_error:         'A server error occurred. Please try again in a moment.',
    access_denied:    'Access was denied. Please allow the required permissions.',
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: dark ? '#0d0f11' : '#f5f5f7',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'var(--font-ui)',
    }}>
      {/* Dark mode toggle — top right */}
      <button
        onClick={() => {
          setDark(d => {
            const next = !d;
            document.documentElement.classList.toggle('dark', next);
            try { localStorage.setItem('tt-theme', next ? 'dark' : 'light'); } catch {}
            return next;
          });
        }}
        style={{
          position: 'fixed', top: 16, right: 16,
          width: 32, height: 32, borderRadius: '50%',
          border: '0.5px solid rgba(128,128,128,0.3)',
          background: dark ? '#1a1d20' : '#e8e8ea',
          color: dark ? '#aaa' : '#555',
          cursor: 'pointer', fontSize: 14,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {dark ? '☀' : '◑'}
      </button>

      {/* Card */}
      <div style={{
        background: dark ? '#161a1e' : '#ffffff',
        border: `0.5px solid ${dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)'}`,
        borderRadius: 16,
        padding: '48px 52px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0,
        boxShadow: dark
          ? '0 24px 64px rgba(0,0,0,0.5)'
          : '0 8px 32px rgba(0,0,0,0.1)',
        maxWidth: 400,
        width: '90vw',
      }}>
        {/* Logo / wordmark */}
        <div style={{
          fontFamily: 'var(--font-ui)',
          fontWeight: 800,
          fontSize: 22,
          color: dark ? '#e8eaed' : '#1a1a1a',
          letterSpacing: '0.06em',
          marginBottom: 6,
        }}>
          TRADING TERMINAL
        </div>

        <div style={{
          fontSize: 11,
          color: dark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.4)',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          marginBottom: 40,
        }}>
          NSE · F&amp;O · AI
        </div>

        {/* Auth error */}
        {authError && (
          <div style={{
            background: 'rgba(239,68,68,0.12)',
            border: '0.5px solid rgba(239,68,68,0.35)',
            borderRadius: 8,
            padding: '10px 16px',
            fontSize: 12,
            color: '#ef4444',
            marginBottom: 24,
            textAlign: 'center',
            width: '100%',
            boxSizing: 'border-box',
          }}>
            {errorMessages[authError] || 'Sign-in failed. Please try again.'}
          </div>
        )}

        {/* Google sign-in button */}
        <button
          onClick={handleLogin}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 24px',
            borderRadius: 10,
            border: `0.5px solid ${dark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)'}`,
            background: dark ? '#1f2328' : '#ffffff',
            color: dark ? '#e8eaed' : '#1a1a1a',
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 600,
            fontFamily: 'var(--font-ui)',
            letterSpacing: '0.01em',
            transition: 'all 0.15s',
            width: '100%',
            justifyContent: 'center',
            boxShadow: dark
              ? '0 2px 8px rgba(0,0,0,0.3)'
              : '0 1px 4px rgba(0,0,0,0.1)',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.border = '0.5px solid rgba(66,133,244,0.6)';
            e.currentTarget.style.boxShadow = dark
              ? '0 4px 16px rgba(66,133,244,0.2)'
              : '0 4px 12px rgba(66,133,244,0.15)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.border = `0.5px solid ${dark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)'}`;
            e.currentTarget.style.boxShadow = dark
              ? '0 2px 8px rgba(0,0,0,0.3)'
              : '0 1px 4px rgba(0,0,0,0.1)';
          }}
        >
          {/* Google logo SVG */}
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Sign in with Google
        </button>

        <div style={{
          marginTop: 28,
          fontSize: 11,
          color: dark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.3)',
          textAlign: 'center',
          lineHeight: 1.6,
        }}>
          Access is by invitation only.<br/>
          Your request will be reviewed after sign-in.
        </div>
      </div>
    </div>
  );
}
